import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import EmailTemplateService from '../../../utils/emailTemplateService'; 

const TemplateEditor = ({ template, onSave, saving, onCancel }) => {
  const [formData, setFormData] = useState({
    template_type: '',
    name: '',
    subject_template: '',
    html_template: '',
    text_template: '',
    variables: [],
    is_active: true
  });
  const [activeEditor, setActiveEditor] = useState('subject'); // 'subject', 'html', 'text'
  const [validationErrors, setValidationErrors] = useState({});
  const [showVariableHelper, setShowVariableHelper] = useState(false);

  // Initialize form data when template changes
  useEffect(() => {
    if (template) {
      setFormData({
        template_type: template.template_type || '',
        name: template.name || '',
        subject_template: template.subject_template || '',
        html_template: template.html_template || '',
        text_template: template.text_template || '',
        variables: Array.isArray(template.variables) ? template.variables : [],
        is_active: template.is_active !== undefined ? template.is_active : true
      });
    } else {
      // Reset form for new template
      setFormData({
        template_type: '',
        name: '',
        subject_template: '',
        html_template: '',
        text_template: '',
        variables: [],
        is_active: true
      });
    }
    setValidationErrors({});
  }, [template]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.template_type.trim()) {
      errors.template_type = 'Template type is required';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }
    
    if (!formData.subject_template.trim()) {
      errors.subject_template = 'Subject template is required';
    }
    
    if (!formData.html_template.trim()) {
      errors.html_template = 'HTML template is required';
    }

    // Validate template syntax
    const subjectValidation = EmailTemplateService.validateTemplate(formData.subject_template);
    if (!subjectValidation.isValid) {
      errors.subject_template = `Subject template errors: ${subjectValidation.errors.join(', ')}`;
    }

    const htmlValidation = EmailTemplateService.validateTemplate(formData.html_template);
    if (!htmlValidation.isValid) {
      errors.html_template = `HTML template errors: ${htmlValidation.errors.join(', ')}`;
    }

    if (formData.text_template) {
      const textValidation = EmailTemplateService.validateTemplate(formData.text_template);
      if (!textValidation.isValid) {
        errors.text_template = `Text template errors: ${textValidation.errors.join(', ')}`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const templateData = {
      ...formData,
      variables: formData.variables.length > 0 ? formData.variables : []
    };

    if (template) {
      // Update existing template
      onSave(template.template_type, templateData);
    } else {
      // Create new template (would need to be implemented in the service)
      onSave(formData.template_type, templateData);
    }
  };

  const extractVariablesFromTemplate = (templateText) => {
    const variableRegex = /{{(\w+)}}/g;
    const variables = new Set();
    let match;
    
    while ((match = variableRegex.exec(templateText)) !== null) {
      variables.add(match[1]);
    }
    
    return Array.from(variables);
  };

  const handleAutoExtractVariables = () => {
    const allVariables = new Set();
    
    // Extract from all template fields
    extractVariablesFromTemplate(formData.subject_template).forEach(v => allVariables.add(v));
    extractVariablesFromTemplate(formData.html_template).forEach(v => allVariables.add(v));
    if (formData.text_template) {
      extractVariablesFromTemplate(formData.text_template).forEach(v => allVariables.add(v));
    }
    
    setFormData(prev => ({
      ...prev,
      variables: Array.from(allVariables)
    }));
  };

  const templateTypeOptions = EmailTemplateService.getTemplateTypeOptions();

  const editorTabs = [
    { id: 'subject', label: 'Subject Line', icon: 'Type' },
    { id: 'html', label: 'HTML Template', icon: 'Code' },
    { id: 'text', label: 'Text Template', icon: 'FileText' }
  ];

  const commonVariables = [
    'user_name', 'user_email', 'full_name', 'dashboard_url', 'ticket_id', 'ticket_url',
    'admin_name', 'admin_message', 'subject', 'status', 'plan_name',
    'amount', 'payment_date', 'next_billing_date', 'unsubscribe_url'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {template ? 'Edit Template' : 'Create New Template'}
          </h2>
          <p className="text-muted-foreground mt-1">
            {template ? `Editing: ${template.name}` : 'Create a new email template'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="HelpCircle"
            onClick={() => setShowVariableHelper(!showVariableHelper)}
          >
            Variable Helper
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            iconName="Save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template Settings */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Template Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Template Type
                </label>
                <Select
                  value={formData.template_type}
                  onChange={(value) => handleInputChange('template_type', value)}
                  options={[
                    { value: '', label: 'Select template type...' },
                    ...templateTypeOptions.map(opt => ({
                      value: opt.value,
                      label: `${opt.label} - ${opt.description}`
                    }))
                  ]}
                  disabled={!!template} // Can't change type of existing template
                />
                {validationErrors.template_type && (
                  <p className="text-destructive text-sm mt-1">{validationErrors.template_type}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Template Name
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter template name..."
                />
                {validationErrors.name && (
                  <p className="text-destructive text-sm mt-1">{validationErrors.name}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.is_active}
                  onChange={(checked) => handleInputChange('is_active', checked)}
                />
                <label className="text-sm font-medium text-foreground">
                  Active Template
                </label>
              </div>
            </div>
          </div>

          {/* Variables */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">Template Variables</h3>
              <Button
                variant="outline"
                size="sm"
                iconName="Zap"
                onClick={handleAutoExtractVariables}
              >
                Auto Extract
              </Button>
            </div>
            
            <div className="space-y-2">
              {formData.variables.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.variables.map((variable, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                    >
                      <span>{`{{${variable}}}`}</span>
                      <button
                        onClick={() => {
                          const newVariables = formData.variables.filter((_, i) => i !== index);
                          handleInputChange('variables', newVariables);
                        }}
                        className="ml-1 text-primary/60 hover:text-primary"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No variables detected. Use the "Auto Extract" button to find variables in your templates.
                </p>
              )}
            </div>
          </div>

          {/* Variable Helper */}
          {showVariableHelper && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Common Variables</h3>
              <div className="space-y-2">
                {commonVariables.map((variableName) => (
                  <button
                    key={variableName}
                    onClick={() => {
                      // Insert variable at cursor position in active editor
                      const variableText = `{{${variableName}}}`;
                      const currentText = formData[`${activeEditor}_template`] || '';
                      handleInputChange(`${activeEditor}_template`, currentText + variableText);
                    }}
                    className="block w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <code className="text-primary">{`{{${variableName}}}`}</code>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Editor Tabs */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6">
                {editorTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveEditor(tab.id)}
                    className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeEditor === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Editor Content */}
            <div className="p-6">
              {activeEditor === 'subject' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Subject Line Template
                  </label>
                  <Input
                    value={formData.subject_template}
                    onChange={(e) => handleInputChange('subject_template', e.target.value)}
                    placeholder="Enter subject line template..."
                  />
                  {validationErrors.subject_template && (
                    <p className="text-destructive text-sm mt-1">{validationErrors.subject_template}</p>
                  )}
                  <p className="text-muted-foreground text-sm mt-2">
                    Use variables like {{user_name}} or {{ticket_id}} to personalize the subject line.
                  </p>
                </div>
              )}

              {activeEditor === 'html' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    HTML Template
                  </label>
                  <textarea
                    value={formData.html_template}
                    onChange={(e) => handleInputChange('html_template', e.target.value)}
                    placeholder="Enter HTML template..."
                    rows={20}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {validationErrors.html_template && (
                    <p className="text-destructive text-sm mt-1">{validationErrors.html_template}</p>
                  )}
                  <p className="text-muted-foreground text-sm mt-2">
                    Full HTML email template. Use {{variable}} syntax for dynamic content.
                  </p>
                </div>
              )}

              {activeEditor === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Plain Text Template (Optional)
                  </label>
                  <textarea
                    value={formData.text_template}
                    onChange={(e) => handleInputChange('text_template', e.target.value)}
                    placeholder="Enter plain text template..."
                    rows={15}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {validationErrors.text_template && (
                    <p className="text-destructive text-sm mt-1">{validationErrors.text_template}</p>
                  )}
                  <p className="text-muted-foreground text-sm mt-2">
                    Plain text version of the email for clients that don't support HTML.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
